/**
 * Condenser — removes heat to condense steam.
 *
 * Default: condenses to saturated liquid (Q=0) at P_cond.
 * Optional: subcooling below saturation by ΔT_subcool.
 * Optional: partial condensation to specified outlet quality x_out.
 */
import type { NSComponent, Junction, ComponentUpdate } from '../types';
import { saturationEnthalpies, propsFromPT_SI } from '../core/fluidAdapter';
import { solvePx } from 'iapws-if97';

export class NSCondenser implements NSComponent {
  id: string;
  type = 'condenser';
  params: Record<string, any>;

  constructor(id: string, params: {
    P_cond?: number;        // Pa — condensing pressure (default 12350 Pa ≈ 50°C)
    dP_frac?: number;       // fractional pressure drop (default 0.0193)
    dT_subcool?: number;    // K — subcooling below T_sat (default 0 = saturated liquid)
    x_out?: number;         // outlet quality 0-1 (default 0 = saturated liquid)
  }) {
    this.id = id;
    this.params = { P_cond: 12350, dP_frac: 0.0193, dT_subcool: 0, x_out: 0, ...params };
  }

  applyBoundaryConditions(inlet: Junction, _outlet: Junction, _dt: number): ComponentUpdate {
    const { P_cond, dP_frac, dT_subcool, x_out } = this.params;
    const P_out = P_cond * (1 - dP_frac);

    try {
      let h_out: number;

      if (x_out != null && x_out > 0 && x_out < 1) {
        // Partial condensation: outlet at specified quality
        const P_out_MPa = P_out / 1e6;
        const state = solvePx(P_out_MPa, x_out);
        h_out = state.enthalpy * 1e3; // kJ/kg → J/kg
      } else if (dT_subcool > 0) {
        // Subcooled outlet: T_out = T_sat - dT_subcool
        const P_out_MPa = P_out / 1e6;
        const sat = solvePx(P_out_MPa, 0);
        const T_sat = sat.temperature; // K
        const T_out = T_sat - dT_subcool;
        const props = propsFromPT_SI(P_out, T_out);
        h_out = props.h;
      } else {
        // Default: saturated liquid (Q=0)
        const [h_f] = saturationEnthalpies(P_out);
        h_out = h_f;
      }

      const deltaH = h_out - inlet.h; // Negative (removing heat)
      const Q_reject = Math.max(0, inlet.h - h_out); // Heat per kg removed (positive, J/kg)

      // Side-channel mirror for the UI sync (stateSync multiplies Q_reject by
      // the solved mass flow to report the APPLIED rejection duty in MW).
      // Never read by the solve — mirrors the NSPump/_lastMetadata convention.
      const metadata = { Q_reject, P_out, h_out, x_out: x_out ?? 0 };
      this.params._lastMetadata = metadata;

      return {
        deltaP: P_out - inlet.P,
        deltaH,
        momentumSource: 0,
        energySource: 0,
        metadata,
      };
    } catch {
      return { deltaP: 0, deltaH: 0, momentumSource: 0, energySource: 0, metadata: {} };
    }
  }
}
